import React, { useCallback, useState } from 'react';
import { Select, Input, InputNumber, Tag, Tooltip } from 'antd';
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'utils/dayjs';
import useCPTCodeOptions from 'hooks/useCPTCodeOptions';
import { TBillingCodeConfig, TVisitBillingCodeEntry } from 'types/insuranceBilling';
import s from './s.module.less';

let nextId = -1;

const UNIT_BASIS_OPTIONS = [
    { label: 'Per session', value: 'PER_SESSION' },
    { label: 'Per item/administration', value: 'PER_ITEM_ADMINISTRATION' },
    { label: 'Timed', value: 'TIMED' },
];

const ADD_NEW_SENTINEL = '__ADD_NEW__';

type CPTCodeSelectProps = {
    value?: string;
    options: TBillingCodeConfig[];
    onSelect: (value: string) => void;
    onAddCustom: (searchValue: string) => void;
};

const CPTCodeSelect = ({ value, options, onSelect, onAddCustom }: CPTCodeSelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const [customCodes, setCustomCodes] = useState<string[]>([]);

    const allOptions = React.useMemo(() => {
        const items: { label: React.ReactNode; value: string }[] = [];
        const addedValues = new Set<string>();
        if (value && !options.some((opt) => opt.code === value) && !customCodes.includes(value)) {
            items.push({ label: value, value });
            addedValues.add(value);
        }
        customCodes.forEach((code) => {
            if (!options.some((opt) => opt.code === code) && !addedValues.has(code)) {
                items.push({ label: code, value: code });
                addedValues.add(code);
            }
        });
        options.forEach((opt) => {
            items.push({ label: `${opt.code} — ${opt.name}`, value: opt.code });
        });
        return items;
    }, [value, customCodes, options]);

    const displayOptions = React.useMemo(() => {
        if (!searchValue) return allOptions;
        const search = searchValue.toLowerCase();
        const filtered = allOptions.filter((opt) => String(opt.label).toLowerCase().includes(search));
        if (filtered.length === 0) {
            return [
                {
                    label: (
                        <span style={{ fontSize: '12px' }}>
                            Code not found. <span className={s.addNewLink}>Add new code</span>
                        </span>
                    ),
                    value: ADD_NEW_SENTINEL,
                },
            ];
        }
        return filtered;
    }, [allOptions, searchValue]);

    const handleSelect = (val: string) => {
        if (val === ADD_NEW_SENTINEL) {
            const trimmed = searchValue.trim();
            if (trimmed) {
                setCustomCodes((prev) => [...prev, trimmed]);
                onAddCustom(trimmed);
            }
        } else {
            onSelect(val);
        }
        setSearchValue('');
    };

    return (
        <Select
            showSearch
            value={value}
            placeholder="Search or enter a CPT code"
            searchValue={searchValue}
            onSearch={setSearchValue}
            onSelect={handleSelect}
            filterOption={false}
            optionLabelProp="value"
            className={s.selectFull}
            notFoundContent={null}
            options={displayOptions}
        />
    );
};

type Props = {
    value: TVisitBillingCodeEntry[];
    onChange: (entries: TVisitBillingCodeEntry[]) => void;
    specialty?: string;
    // 会中场景：整块加卡片边框，footer（Save 按钮）渲染在边框内底部
    bordered?: boolean;
    footer?: React.ReactNode;
};

const BillingCodesSection = ({ value, onChange, specialty, bordered, footer }: Props) => {
    const [cptOptions] = useCPTCodeOptions(specialty);

    const handleSelectCode = useCallback(
        (entryId: number, cptCode: string) => {
            const matched = cptOptions.find((opt) => opt.code === cptCode);
            if (matched) {
                const next = value.map((entry) =>
                    entry.id === entryId
                        ? {
                              ...entry,
                              cptCode: matched.code,
                              description: matched.name,
                              isCustom: false,
                          }
                        : entry,
                );
                onChange(next);
            }
        },
        [cptOptions, value, onChange],
    );

    const handleAddCustomCode = useCallback(
        (entryId: number, searchValue: string) => {
            const trimmed = searchValue.trim();
            if (trimmed) {
                const next = value.map((entry) =>
                    entry.id === entryId
                        ? {
                              ...entry,
                              cptCode: trimmed,
                              description: '',
                              isCustom: true,
                          }
                        : entry,
                );
                onChange(next);
            }
        },
        [value, onChange],
    );

    const handleUpdate = useCallback(
        (entryId: number, updates: Partial<TVisitBillingCodeEntry>) => {
            const next = value.map((entry) => (entry.id === entryId ? { ...entry, ...updates } : entry));
            onChange(next);
        },
        [value, onChange],
    );

    const handleAdd = useCallback(() => {
        onChange([
            ...value,
            {
                id: nextId--,
                description: '',
                unitBasis: 'PER_SESSION',
                units: 1,
                isPrimary: value.length === 0,
            },
        ]);
    }, [value, onChange]);

    const handleRemove = useCallback(
        (id: number) => {
            let next = value.filter((entry) => entry.id !== id);
            if (next.length > 0 && !next.some((e) => e.isPrimary)) {
                next = next.map((e, i) => (i === 0 ? { ...e, isPrimary: true } : e));
            }
            onChange(next);
        },
        [value, onChange],
    );

    const claimTotal = value.reduce((sum, entry) => {
        if (entry.cptCode && entry.insuranceRate) {
            return sum + entry.insuranceRate * entry.units;
        }
        return sum;
    }, 0);

    return (
        <div className={`${s.wrap} ${bordered ? s.bordered : ''}`}>
            <div className={s.header}>
                <h3 className={s.title}>Billing codes</h3>
                <p className={s.subtitle}>
                    Select the codes for the services performed at this visit and confirm the units. At least one
                    primary code is required before Klarity can submit a claim.
                </p>
            </div>
            {value.map((entry) => {
                const selectedCodes = value.filter((e) => e.id !== entry.id && e.cptCode).map((e) => e.cptCode);
                const filteredOptions = cptOptions.filter((opt) => !selectedCodes.includes(opt.code));

                return (
                    <div key={entry.id} className={s.codeCard}>
                        <div className={s.cardHeader}>
                            <Tag className={entry.isPrimary ? s.primaryTag : s.addOnTag}>
                                {entry.isPrimary ? 'Primary' : 'Add-on'}
                            </Tag>
                            {!entry.isPrimary && (
                                <span className={s.removeBtn} onClick={() => handleRemove(entry.id)}>
                                    Remove
                                </span>
                            )}
                        </div>
                        <div className={s.cardBody}>
                            <div className={s.row}>
                                <div className={s.cptField}>
                                    <div className={s.fieldLabel}>
                                        CPT code
                                        <Tooltip title="The CPT billing code for this service.">
                                            <QuestionCircleOutlined className={s.tooltipIcon} />
                                        </Tooltip>
                                    </div>
                                    <CPTCodeSelect
                                        value={entry.cptCode}
                                        options={filteredOptions}
                                        onSelect={(val) => handleSelectCode(entry.id, val)}
                                        onAddCustom={(val) => handleAddCustomCode(entry.id, val)}
                                    />
                                </div>
                                <div className={s.descField}>
                                    <div className={s.fieldLabel}>Description</div>
                                    <Input
                                        value={entry.description}
                                        readOnly={!entry.isCustom}
                                        onChange={(e) =>
                                            entry.isCustom && handleUpdate(entry.id, { description: e.target.value })
                                        }
                                        className={s.descInput}
                                    />
                                </div>
                            </div>
                            <div className={s.row}>
                                <div className={s.basisField}>
                                    <div className={s.fieldLabel}>
                                        Unit basis
                                        <Tooltip title="How the service is measured for billing purposes.">
                                            <QuestionCircleOutlined className={s.tooltipIcon} />
                                        </Tooltip>
                                    </div>
                                    <Select
                                        value={entry.unitBasis}
                                        onChange={(val) => handleUpdate(entry.id, { unitBasis: val })}
                                        options={UNIT_BASIS_OPTIONS}
                                        className={s.selectFull}
                                    />
                                </div>
                                <div className={s.unitsField}>
                                    <div className={s.fieldLabel}>Units</div>
                                    <InputNumber
                                        value={entry.units}
                                        min={1}
                                        onChange={(val) => handleUpdate(entry.id, { units: val ?? 1 })}
                                        controls={false}
                                        className={s.unitsInput}
                                    />
                                </div>
                                <div className={s.rateField}>
                                    <div className={s.fieldLabel}>
                                        Insurance rate
                                        <Tooltip title="The rate you charge insurance carriers for this service.">
                                            <QuestionCircleOutlined className={s.tooltipIcon} />
                                        </Tooltip>
                                    </div>
                                    <InputNumber
                                        value={entry.insuranceRate}
                                        onChange={(val) => handleUpdate(entry.id, { insuranceRate: val ?? undefined })}
                                        precision={2}
                                        prefix="$"
                                        controls={false}
                                        className={s.rateInput}
                                    />
                                </div>
                            </div>
                        </div>
                        {entry.lastUsedAt && (
                            <div className={s.lastUsed}>
                                Last used{' '}
                                <span className={s.lastUsedDate}>{dayjs(entry.lastUsedAt).format('MMM DD, YYYY')}</span>
                            </div>
                        )}
                    </div>
                );
            })}
            <div className={s.footer}>
                <span className={s.addBtn} onClick={handleAdd}>
                    <PlusOutlined /> Add code
                </span>
                {value.length > 0 && (
                    <span className={s.claimTotal}>
                        Claim total <strong>${claimTotal.toFixed(2)}</strong>
                    </span>
                )}
            </div>
            {footer}
        </div>
    );
};

export default BillingCodesSection;
